import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Empty, Input, message, Modal, Pagination, Space, Spin, Typography } from 'antd';
import { CloudUploadOutlined, ReloadOutlined } from '@ant-design/icons';
import ErrorBoundary from '../../components/ErrorBoundary';
import UploadItem from '../../components/UploadItem';
import { getId, uploadHtmlContent } from '../../helpers/file';
import { useReuploadPolicy } from '../../hooks/useReuploadPolicy';
import { useTranslation } from '../../hooks/useTranslation';
import { showUploadErrorModal } from '../../utils/uploadErrorModal';
import './index.css';

interface UploadRecord {
  id: string;
  dir?: string;
  url: string;
  rawUrl?: string;
  contentType?: string;
  customDomain?: string | null;
  fileName: string;
  time: string | number;
  size?: string;
  illicit?: number;
  illicitDetails?: string;
  storageType?: string;
  expireTime?: string | null;
}

export const buildDefaultHtmlFileName = (date = new Date()) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  const datePart = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
  const timePart = `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  return `page-${datePart}-${timePart}.html`;
};

export const isValidHtmlFileName = (fileName = '') => {
  const trimmed = fileName.trim();
  return /^[^\\/]+\.(html|htm)$/i.test(trimmed);
};

const GOOGLE_FONTS_CSS2_URL = 'https://fonts.googleapis.com/css2';

const WebHosting: React.FC = () => {
  const { t } = useTranslation();
  const { policy: reuploadPolicy } = useReuploadPolicy();
  const tRef = useRef(t);
  const [fileName, setFileName] = useState(buildDefaultHtmlFileName);
  const [htmlContent, setHtmlContent] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<UploadRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [searchText, setSearchText] = useState('');
  const [lastUploadUrl, setLastUploadUrl] = useState('');
  const [userInfo, setUserInfo] = useState<Record<string, any>>({});

  const trimmedFileName = fileName.trim();
  const canUpload = useMemo(() => {
    return htmlContent.trim().length > 0 && isValidHtmlFileName(trimmedFileName) && !uploading;
  }, [htmlContent, trimmedFileName, uploading]);
  const hasGoogleFontsResource = useMemo(() => {
    return htmlContent.includes(GOOGLE_FONTS_CSS2_URL);
  }, [htmlContent]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const loadHistory = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      uid: getId() || '',
      page: String(current),
      pageSize: String(pageSize),
      fileType: 'html',
    });
    if (keyword.trim()) {
      params.set('keyword', keyword.trim());
    }

    fetch(`/img/history?${params.toString()}`)
      .then(res => res.json())
      .then(pageData => {
        setList(pageData.data || []);
        setTotal(pageData.total || 0);
      })
      .catch(error => {
        console.error('加载网页托管列表失败:', error);
        message.error(tRef.current('webHosting.listLoadFailed'));
      })
      .finally(() => setLoading(false));
  }, [current, pageSize, keyword]);

  useEffect(() => {
    fetch('/login/info', { method: 'GET' })
      .then(res => res.json())
      .then(data => {
        if (!data.isSuccess) {
          return;
        }
        const info = data.detail || data;
        if (info.config && typeof info.config === 'string') {
          try {
            info.config = JSON.parse(info.config);
          } catch (error) {
            console.error('解析用户配置失败:', error);
          }
        }
        setUserInfo(info);
      })
      .catch(error => {
        console.error('获取用户信息失败:', error);
      });
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleUpload = async () => {
    if (!htmlContent.trim()) {
      message.warning(t('webHosting.emptyContent'));
      return;
    }
    if (!isValidHtmlFileName(trimmedFileName)) {
      message.warning(t('webHosting.invalidFileName'));
      return;
    }

    setUploading(true);
    try {
      const record = await uploadHtmlContent(
        trimmedFileName,
        htmlContent,
        (percent) => {
          let content = t('upload.uploadProgress', { percent });
          if (percent === 100) {
            content = t('upload.uploadComplete');
          }
          message.info({ key: 'webHostingUpload', duration: 1000, content });
        },
        (errorMessage, failedFileName) => {
          showUploadErrorModal(t, errorMessage, failedFileName);
        }
      );

      message.success({ key: 'webHostingUpload', content: t('webHosting.uploadSuccess') });
      setLastUploadUrl(record?.url || '');
      setUploadModalOpen(false);
      setFileName(buildDefaultHtmlFileName());
      setHtmlContent('');
      setCurrent(1);
      if (current === 1) {
        loadHistory();
      }
    } catch (error) {
      console.error('上传 HTML 失败:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = (value: string) => {
    setKeyword(value.trim());
    setCurrent(1);
  };

  const handleOpenUploadModal = () => {
    if (!htmlContent.trim()) {
      setFileName(buildDefaultHtmlFileName());
    }
    setUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    if (!uploading) {
      setUploadModalOpen(false);
    }
  };

  return (
    <div className="web-hosting-page">
      <Alert
        message={t('webHosting.subtitle')}
        type="info"
        showIcon
        style={{ marginBottom: '12px' }}
      />

      <div className="web-hosting-list-toolbar">
        <Input.Search
          allowClear
          className="web-hosting-search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onSearch={handleSearch}
          placeholder={t('webHosting.searchPlaceholder')}
        />
        <Space className="web-hosting-toolbar-actions" wrap>
          <Button icon={<ReloadOutlined />} onClick={loadHistory} loading={loading}>
            {t('webHosting.refreshList')}
          </Button>
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={handleOpenUploadModal}
          >
            {t('webHosting.uploadEntry')}
          </Button>
        </Space>
      </div>

      {lastUploadUrl && (
        <Alert
          className="web-hosting-latest-alert"
          message={t('webHosting.latestUpload')}
          description={<a href={lastUploadUrl} target="_blank" rel="noopener noreferrer">{lastUploadUrl}</a>}
          type="success"
          showIcon
        />
      )}

      <Spin spinning={loading}>
        {list.length === 0 ? (
          <Empty description={t('webHosting.emptyList')} />
        ) : (
          <div className="list web-hosting-list">
            {list.map((item, index) => (
              <ErrorBoundary key={item.id}>
                <UploadItem
                  dir={item.dir}
                  index={index}
                  userInfo={userInfo}
                  url={item.url}
                  rawUrl={item.rawUrl}
                  contentType={item.contentType}
                  customDomain={item.customDomain || ''}
                  id={item.id}
                  fileName={item.fileName}
                  time={item.time}
                  size={item.size}
                  illicit={item.illicit}
                  illicitDetails={item.illicitDetails}
                  storageType={item.storageType}
                  expireTime={item.expireTime}
                  onDelete={loadHistory}
                  onRefresh={loadHistory}
                  selected={false}
                  onSelect={() => undefined}
                  selectedIds={[]}
                  reuploadPolicy={reuploadPolicy}
                  selectable={false}
                />
              </ErrorBoundary>
            ))}
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
            <i className="hiddenUploadItem" style={{ width: '324px' }} />
          </div>
        )}
      </Spin>

      <div className="web-hosting-pagination">
        <Pagination
          current={current}
          size="small"
          showSizeChanger
          pageSizeOptions={['10', '20', '50', '100']}
          total={total}
          showTotal={(nextTotal) => t('common.totalRecords', { total: nextTotal })}
          pageSize={pageSize}
          onChange={(page, nextPageSize) => {
            setCurrent(page);
            setPageSize(nextPageSize);
          }}
        />
      </div>

      <Modal
        title={t('webHosting.uploadModalTitle')}
        open={uploadModalOpen}
        width={760}
        getContainer={false}
        maskClosable={!uploading}
        keyboard={!uploading}
        onCancel={handleCloseUploadModal}
        footer={[
          <Button key="cancel" onClick={handleCloseUploadModal} disabled={uploading}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="upload"
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={uploading}
            disabled={!canUpload}
            onClick={handleUpload}
          >
            {t('webHosting.uploadButton')}
          </Button>,
        ]}
      >
        <Space direction="vertical" size={14} style={{ width: '100%' }}>
          <Alert message={t('webHosting.uploadNotice')} type="info" showIcon />
          <Input
            value={fileName}
            onChange={(event) => setFileName(event.target.value)}
            addonBefore={t('webHosting.fileName')}
            status={trimmedFileName && !isValidHtmlFileName(trimmedFileName) ? 'error' : undefined}
            placeholder="page.html"
          />
          <Input.TextArea
            value={htmlContent}
            onChange={(event) => setHtmlContent(event.target.value)}
            rows={14}
            placeholder={t('webHosting.htmlPlaceholder')}
            className="web-hosting-editor"
          />
          {hasGoogleFontsResource && (
            <Alert
              className="web-hosting-google-fonts-alert"
              message={t('webHosting.googleFontsWarningTitle')}
              description={t('webHosting.googleFontsWarningDescription')}
              type="warning"
              showIcon
            />
          )}
          <div className="web-hosting-upload-actions">
            <Typography.Text type="secondary">
              {t('webHosting.contentSize', { count: new Blob([htmlContent]).size })}
            </Typography.Text>
            <Button onClick={() => setFileName(buildDefaultHtmlFileName())}>
              {t('webHosting.resetFileName')}
            </Button>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default WebHosting;
